import { CONTENT_VALIDATION, WINDOW_TYPE_OPTIONS, type WindowType } from '@find-my-part/types';

export const FMP_HISTORY_DRAFT_KEY = 'fmpDraftV1';
export const FMP_HISTORY_DRAFT_MAX_AGE_MS = 30 * 60 * 1_000;
export const FMP_HISTORY_DRAFT_TOMBSTONES_KEY = 'fmpDraftTombstonesV1';
const MAX_DRAFT_TOMBSTONES = 32;

const WINDOW_TYPES = new Set<WindowType>(WINDOW_TYPE_OPTIONS.map((option) => option.value));
const DRAFT_KEYS = new Set([
  'version',
  'tenantKey',
  'draftNonce',
  'savedAtEpochMs',
  'title',
  'body',
  'windowType',
]);
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface FmpHistoryStateV1 {
  version: 1;
  tenantKey: string;
  draftNonce: string;
  savedAtEpochMs: number;
  title: string;
  body: string;
  windowType?: WindowType;
}

export interface FmpDraftInput {
  tenantKey: string;
  draftNonce: string;
  title: string;
  body: string;
  windowType?: WindowType | '';
}

export type FmpHistory = Pick<History, 'state' | 'replaceState'>;
export type FmpSessionStorage = Pick<Storage, 'getItem' | 'setItem'>;

interface FmpDraftTombstoneV1 {
  version: 1;
  tenantKey: string;
  draftNonce: string;
  savedAtEpochMs: number;
  expiresAtEpochMs: number;
}

interface FmpHistoryDraftIdentity {
  tenantKey: string;
  draftNonce: string;
  savedAtEpochMs: number;
}

export function isPlainHistoryState(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

export function createFmpDraftNonce(
  randomUuid: () => string = () => crypto.randomUUID()
): string {
  return randomUuid();
}

function replaceMergedState(history: FmpHistory, state: Record<string, unknown>): boolean {
  try {
    history.replaceState(state, '');
    return true;
  } catch {
    return false;
  }
}

function readDraftTombstones(
  storage: FmpSessionStorage,
  nowEpochMs: number
): FmpDraftTombstoneV1[] {
  try {
    const parsed = JSON.parse(storage.getItem(FMP_HISTORY_DRAFT_TOMBSTONES_KEY) ?? '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is FmpDraftTombstoneV1 =>
      isPlainHistoryState(value)
      && value.version === 1
      && typeof value.tenantKey === 'string'
      && value.tenantKey.length > 0
      && typeof value.draftNonce === 'string'
      && UUID_V4_PATTERN.test(value.draftNonce)
      && typeof value.savedAtEpochMs === 'number'
      && Number.isSafeInteger(value.savedAtEpochMs)
      && typeof value.expiresAtEpochMs === 'number'
      && Number.isSafeInteger(value.expiresAtEpochMs)
      && value.expiresAtEpochMs >= nowEpochMs
    );
  } catch {
    return [];
  }
}

/** Record a content-free replay tombstone when history cleanup is unavailable. */
export function tombstoneFmpHistoryDraft(
  storage: FmpSessionStorage,
  tenantKey: string,
  draftNonce: string,
  nowEpochMs: number = Date.now(),
  savedAtEpochMs: number = nowEpochMs
): boolean {
  const expiresAtEpochMs =
    Math.max(nowEpochMs, savedAtEpochMs) + FMP_HISTORY_DRAFT_MAX_AGE_MS;
  if (
    !tenantKey
    || !UUID_V4_PATTERN.test(draftNonce)
    || !Number.isSafeInteger(nowEpochMs)
    || !Number.isSafeInteger(savedAtEpochMs)
    || !Number.isSafeInteger(expiresAtEpochMs)
  ) {
    return false;
  }
  const tombstones = readDraftTombstones(storage, nowEpochMs).filter(
    (item) => item.tenantKey !== tenantKey || item.draftNonce !== draftNonce
  );
  tombstones.push({
    version: 1,
    tenantKey,
    draftNonce,
    savedAtEpochMs,
    expiresAtEpochMs,
  });
  try {
    storage.setItem(
      FMP_HISTORY_DRAFT_TOMBSTONES_KEY,
      JSON.stringify(tombstones.slice(-MAX_DRAFT_TOMBSTONES))
    );
    return true;
  } catch {
    return false;
  }
}

export function isFmpHistoryDraftTombstoned(
  storage: FmpSessionStorage,
  tenantKey: string,
  draftNonce: string,
  savedAtEpochMs: number,
  nowEpochMs: number = Date.now()
): boolean {
  return readDraftTombstones(storage, nowEpochMs).some(
    (item) =>
      item.tenantKey === tenantKey
      && item.draftNonce === draftNonce
      && item.savedAtEpochMs === savedAtEpochMs
  );
}

/**
 * Remove the envelope, or suppress replay with a content-free session
 * tombstone if the host rejects the history mutation.
 */
export function retireFmpHistoryDraft(
  history: FmpHistory,
  storage: FmpSessionStorage | null,
  tenantKey: string | null,
  draftNonce: string | null,
  nowEpochMs: number = Date.now()
): boolean {
  const identity = isPlainHistoryState(history.state)
    ? parseEnvelopeIdentity(history.state[FMP_HISTORY_DRAFT_KEY])
    : null;
  if (clearFmpHistoryDraft(history)) return true;
  const tombstoneTenantKey = identity?.tenantKey ?? tenantKey;
  const tombstoneNonce = identity?.draftNonce ?? draftNonce;
  return Boolean(
    storage
    && tombstoneTenantKey
    && tombstoneNonce
    && tombstoneFmpHistoryDraft(
      storage,
      tombstoneTenantKey,
      tombstoneNonce,
      nowEpochMs,
      identity?.savedAtEpochMs ?? nowEpochMs
    )
  );
}

/** Remove only the reserved FMP key while retaining every host/theme key. */
export function clearFmpHistoryDraft(history: FmpHistory): boolean {
  if (!isPlainHistoryState(history.state)) return history.state === null;
  if (!Object.prototype.hasOwnProperty.call(history.state, FMP_HISTORY_DRAFT_KEY)) return true;

  const nextState = { ...history.state };
  delete nextState[FMP_HISTORY_DRAFT_KEY];
  return replaceMergedState(history, nextState);
}

/** Synchronously persist before same-tab navigation; false means navigation must not tear down. */
export function persistFmpHistoryDraft(
  history: FmpHistory,
  input: FmpDraftInput,
  savedAtEpochMs: number = Date.now()
): boolean {
  if (
    !input.tenantKey
    || !UUID_V4_PATTERN.test(input.draftNonce)
    || !Number.isSafeInteger(savedAtEpochMs)
    || input.title.length > CONTENT_VALIDATION.MAX_TITLE_LENGTH
    || input.body.length > CONTENT_VALIDATION.MAX_DESCRIPTION_LENGTH
    || (input.windowType !== undefined
      && input.windowType !== ''
      && !WINDOW_TYPES.has(input.windowType))
  ) {
    return false;
  }
  if (history.state !== null && !isPlainHistoryState(history.state)) return false;

  const envelope: FmpHistoryStateV1 = {
    version: 1,
    tenantKey: input.tenantKey,
    draftNonce: input.draftNonce,
    savedAtEpochMs,
    title: input.title,
    body: input.body,
    ...(input.windowType ? { windowType: input.windowType } : {}),
  };
  const hostState = history.state ?? {};
  return replaceMergedState(history, { ...hostState, [FMP_HISTORY_DRAFT_KEY]: envelope });
}

function parseEnvelopeIdentity(value: unknown): FmpHistoryDraftIdentity | null {
  if (!isPlainHistoryState(value)) return null;
  if (
    typeof value.tenantKey !== 'string' ||
    value.tenantKey.length === 0 ||
    typeof value.draftNonce !== 'string' ||
    !UUID_V4_PATTERN.test(value.draftNonce) ||
    typeof value.savedAtEpochMs !== 'number' ||
    !Number.isSafeInteger(value.savedAtEpochMs)
  ) {
    return null;
  }
  return {
    tenantKey: value.tenantKey,
    draftNonce: value.draftNonce,
    savedAtEpochMs: value.savedAtEpochMs,
  };
}

function parseEnvelope(value: unknown, nowEpochMs: number): FmpHistoryStateV1 | null {
  const identity = parseEnvelopeIdentity(value);
  if (!identity || !isPlainHistoryState(value)) return null;
  if (Object.keys(value).some((key) => !DRAFT_KEYS.has(key))) return null;
  if (
    value.version !== 1 ||
    typeof value.savedAtEpochMs !== 'number' ||
    !Number.isSafeInteger(value.savedAtEpochMs) ||
    typeof value.title !== 'string' ||
    value.title.length > CONTENT_VALIDATION.MAX_TITLE_LENGTH ||
    typeof value.body !== 'string' ||
    value.body.length > CONTENT_VALIDATION.MAX_DESCRIPTION_LENGTH
  ) {
    return null;
  }

  const ageMs = nowEpochMs - value.savedAtEpochMs;
  if (ageMs < 0 || ageMs > FMP_HISTORY_DRAFT_MAX_AGE_MS) return null;

  if (
    value.windowType !== undefined &&
    (typeof value.windowType !== 'string' || !WINDOW_TYPES.has(value.windowType as WindowType))
  ) {
    return null;
  }

  return value as unknown as FmpHistoryStateV1;
}

/**
 * Consume a valid same-tenant draft exactly once. Invalid, expired, or
 * cross-tenant envelopes are also removed, but host state is always retained.
 */
export function consumeFmpHistoryDraft(
  history: FmpHistory,
  tenantKey: string | null,
  nowEpochMs: number = Date.now(),
  storage: FmpSessionStorage | null = null
): FmpHistoryStateV1 | null {
  if (!isPlainHistoryState(history.state)) return null;
  if (!Object.prototype.hasOwnProperty.call(history.state, FMP_HISTORY_DRAFT_KEY)) return null;

  const parsedEnvelope = parseEnvelope(
    history.state[FMP_HISTORY_DRAFT_KEY],
    nowEpochMs
  );
  const envelopeIdentity = parseEnvelopeIdentity(history.state[FMP_HISTORY_DRAFT_KEY]);
  const envelope = parsedEnvelope?.tenantKey === tenantKey ? parsedEnvelope : null;
  const tombstoned = Boolean(
    envelope
    && storage
    && isFmpHistoryDraftTombstoned(
      storage,
      envelope.tenantKey,
      envelope.draftNonce,
      envelope.savedAtEpochMs,
      nowEpochMs
    )
  );
  // Consumption is a destructive read: never report a draft as restored when
  // the reserved history entry could not be removed. Otherwise a transient or
  // host-imposed replaceState failure can replay the same draft on a later
  // activation. Invalid envelopes still fail closed; a future activation may
  // retry their cleanup without exposing their contents.
  const cleared = clearFmpHistoryDraft(history);
  if (!cleared && envelopeIdentity && storage) {
    // Tombstone the envelope's validated identity, even when it belongs to a
    // different tenant or the current store context is unavailable. Otherwise
    // a host-denied cleanup could restore the stale text when that tenant is
    // visited later in the same tab.
    tombstoneFmpHistoryDraft(
      storage,
      envelopeIdentity.tenantKey,
      envelopeIdentity.draftNonce,
      nowEpochMs,
      envelopeIdentity.savedAtEpochMs
    );
  }
  return cleared && !tombstoned ? envelope : null;
}
